import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { Stack } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox";
import SplitButton from "@entrilia/shared/components/SplitButton";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import useRequest from "@entrilia/shared/hooks/useRequest";
import biApi from "../../../../../../api/biApi";
import type { ReportTemplateDto } from "../../../../../../api/types/svTypes.ts";
import useClientContext from "../../../../../../context/useClientContext";
import { useReportsContext } from "../../contexts/reports/ReportsContext";
import { useReportTemplatesContext } from "../../contexts/ReportTemplatesContext.ts";
import AddGroupDialog from "../../groups/actions/AddGroupDialog.tsx";
import { useGroupsContext } from "../../groups/contexts/GroupsContext.ts";
import ReportsPageContainer from "../components/ReportsPageContainer";
import ReportsSideMenu from "../components/ReportsSideMenu.tsx";
import ReportTemplates from "../components/ReportTemplates";
import ReportsGridWrapper from "../grid/ReportsGridWrapper";
import useReportsWithSideMenu from "../hooks/useReportsWithSideMenu.ts";
import { getEditUiReportUrl } from "../utilities/editReportUrl.ts";

const CompanyUiReportsContainer = () => {
  usePageTitle("Reports");

  const navigate = useNavigate();
  const { clientCode } = useClientContext();
  const { reports } = useReportsContext();
  const { groups: reportGroups, actions: groupActions } = useGroupsContext();
  const { reportTemplates, ui: templatesUi, groupsUi: templateGroupsId } = useReportTemplatesContext();

  const {
    filteredReports,
    groups,
    reportCountsByGroup,
    sideMenuFilter,
    sideMenuFilterType,
    selectedFilterLabel,
    setSideMenuFilter,
    toggleFavorite,
  } = useReportsWithSideMenu({
    reports,
    reportGroups,
    clientCode,
    getFavoriteReports: biApi.uiReports.getFavoriteReports,
    toggleFavoriteReport: biApi.uiReports.toggleFavoriteReport,
  });

  const [showAddGroupDialog, setShowAddGroupDialog] = useState(false);

  const { request: createDraftReport, isLoading: isCreatingDraftReport } = useRequest(
    biApi.uiReports.createDraftReport
  );

  const onCreateDraftReport = useCallback(async () => {
    const [report] = await createDraftReport();
    if (report) {
      navigate(getEditUiReportUrl(clientCode, report.reportId));
    }
  }, [createDraftReport, clientCode, navigate]);

  const [openTemplates, setOpenTemplates] = useState(false);

  const primaryOption = useMemo(() => {
    return {
      title: "Create New",
      handler: onCreateDraftReport,
      icon: AddRoundedIcon,
    };
  }, [onCreateDraftReport]);

  const secondaryOptions = useMemo(() => {
    return [
      primaryOption,
      {
        title: "Create from Template",
        handler: () => setOpenTemplates(true),
        icon: DescriptionOutlinedIcon,
        disabled: reportTemplates.length === 0,
      },
    ];
  }, [primaryOption, reportTemplates.length]);

  return (
    <Stack direction="row" sx={{ height: "100%", width: "100%" }}>
      <ReportsSideMenu
        title="Reports"
        selectedFilter={sideMenuFilter}
        onFilterChange={setSideMenuFilter}
        groups={groups}
        reportCountsByGroup={reportCountsByGroup}
        groupType="report"
        onAddGroup={() => setShowAddGroupDialog(true)}
        onReorderGroups={(groups) => groupActions.reorder(clientCode, groups)}
      />
      <ReportsPageContainer
        title={selectedFilterLabel}
        Actions={
          <>
            <AuthorizedBox permissions={["ManageReports"]}>
              {reportTemplates.length > 0 && (
                <ReportTemplates
                  openTemplates={openTemplates}
                  clientCode={clientCode}
                  onSetTemplateOpen={(v) => setOpenTemplates(v)}
                  templates={reportTemplates as ReportTemplateDto[]}
                  disabled={
                    !templatesUi.loaded ||
                    !templateGroupsId?.loaded ||
                    templatesUi.error !== undefined ||
                    templateGroupsId?.error !== undefined
                  }
                  loading={!templatesUi.loaded || !templateGroupsId?.loaded}
                />
              )}
            </AuthorizedBox>
            <AuthorizedBox permissions={["ManageReports"]}>
              <SplitButton
                saving={isCreatingDraftReport}
                secondaryOptions={secondaryOptions}
                primaryOption={primaryOption}
                primaryActionStartIcon={<AddRoundedIcon />}
              />
            </AuthorizedBox>
          </>
        }
      >
        <ReportsGridWrapper
          reports={filteredReports}
          isGroupSelected={sideMenuFilterType === "group"}
          onToggleFavorite={toggleFavorite}
        />
      </ReportsPageContainer>
      {showAddGroupDialog && <AddGroupDialog clientCode={clientCode} onClose={() => setShowAddGroupDialog(false)} />}
    </Stack>
  );
};

export default CompanyUiReportsContainer;
